import { getBrowserTimeZone } from './sheetAnalytics';

export const DASHBOARD_CONFIG_VERSION = 1;
export const DASHBOARD_TIME_PRESETS = [
  'all', 'last_7_days', 'last_30_days', 'last_90_days',
  'this_month', 'this_quarter', 'this_year', 'custom'
];

const GRID_WIDTHS = [3, 4, 6, 8, 12];
const DATE_TYPES = new Set(['date', 'datetime']);
const NUMBER_TYPES = new Set(['number', 'rating', 'currency', 'percent']);

export const dashboardFieldFamily = (type = '') => {
  if (DATE_TYPES.has(type)) return 'date';
  if (NUMBER_TYPES.has(type)) return 'number';
  return 'category';
};

const slug = (value = '') => `${value}`.trim().toLowerCase()
  .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 54);

const uniqueId = (prefix, seed = '') => `${prefix}_${slug(seed) || Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const clampWidth = (value, fallback = 6) => GRID_WIDTHS.reduce((closest, width) => (
  Math.abs(width - Number(value || fallback)) < Math.abs(closest - Number(value || fallback)) ? width : closest
), fallback);

const analyticKey = (sheetId, analyticId) => `${sheetId}:${analyticId}`;

export const getWorkspaceAnalyticsCatalog = (sheets = []) => (Array.isArray(sheets) ? sheets : [])
  .flatMap((sheet) => (Array.isArray(sheet?.analytics) ? sheet.analytics : []).map((analytic) => ({
    ...analytic,
    sheetId: sheet.id,
    sheetTitle: sheet.title || 'Untitled sheet',
    sheetPrimaryColor: sheet.primaryColor || '',
    key: analyticKey(sheet.id, analytic.id)
  })))
  .filter((analytic) => analytic.id && analytic.title && ['kpi', 'chart'].includes(analytic.type));

export const createDashboardItemForAnalytic = (analytic, order = 0) => ({
  id: `visual_${slug(analytic.sheetId)}_${slug(analytic.id)}`,
  type: 'analytic',
  sheetId: analytic.sheetId,
  analyticId: analytic.id,
  order,
  colSpan: analytic.type === 'chart' ? 6 : 3,
  rowSpan: analytic.type === 'chart' ? 2 : 1
});

export const seedWorkspaceDashboardConfig = (sheets = []) => {
  const catalog = getWorkspaceAnalyticsCatalog(sheets);
  const featured = catalog.filter((analytic) => analytic?.placement?.showInWorkspace === true)
    .sort((left, right) => {
      if (left.type !== right.type) return left.type === 'kpi' ? -1 : 1;
      return (Number(left?.placement?.order) || 0) - (Number(right?.placement?.order) || 0);
    });
  return {
    version: DASHBOARD_CONFIG_VERSION,
    defaultTimeRange: { preset: 'all', start: '', end: '', timezone: getBrowserTimeZone() },
    dimensions: [],
    items: [
      { id: 'dashboard_time', type: 'time', order: 0, colSpan: 12, rowSpan: 1 },
      ...featured.map((analytic, index) => createDashboardItemForAnalytic(analytic, index + 1))
    ]
  };
};

export const sanitizeWorkspaceDashboardConfig = (source, sheets = [], { seedLegacy = true, preserveUnknownAnalytics = false } = {}) => {
  if (!source || typeof source !== 'object' || Number(source.version) !== DASHBOARD_CONFIG_VERSION) {
    return seedLegacy ? seedWorkspaceDashboardConfig(sheets) : seedWorkspaceDashboardConfig([]);
  }
  const sheetMap = new Map((Array.isArray(sheets) ? sheets : []).filter((sheet) => sheet?.id).map((sheet) => [sheet.id, sheet]));
  const catalogMap = new Map(getWorkspaceAnalyticsCatalog(sheets).map((analytic) => [analytic.key, analytic]));
  const dimensions = (Array.isArray(source.dimensions) ? source.dimensions : []).slice(0, 20).map((dimension, index) => {
    const id = slug(dimension?.id) || `dimension_${index}`;
    const family = ['category', 'number', 'date'].includes(dimension?.type) ? dimension.type : 'category';
    const mappings = (Array.isArray(dimension?.mappings) ? dimension.mappings : []).filter((mapping) => {
      const sheet = sheetMap.get(mapping?.sheetId);
      const header = sheet?.headers?.find((item) => item.id === mapping?.headerId);
      return (!sheet && preserveUnknownAnalytics) || (header && dashboardFieldFamily(header.type) === family);
    }).filter((mapping, mappingIndex, all) => all.findIndex((item) => item.sheetId === mapping.sheetId) === mappingIndex)
      .map((mapping) => ({ sheetId: mapping.sheetId, headerId: mapping.headerId }));
    return { id, name: `${dimension?.name || `Filter ${index + 1}`}`.trim().slice(0, 80), type: family, mappings };
  }).filter((dimension, index, all) => dimension.name && all.findIndex((item) => item.id === dimension.id) === index);
  const dimensionIds = new Set(dimensions.map((dimension) => dimension.id));
  const seen = new Set();
  const items = (Array.isArray(source.items) ? source.items : []).slice(0, 80).filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    if (item.type === 'analytic' && !preserveUnknownAnalytics && !catalogMap.has(analyticKey(item.sheetId, item.analyticId))) return false;
    if (item.type === 'dimension' && !dimensionIds.has(item.dimensionId)) return false;
    if (!['analytic', 'dimension', 'time'].includes(item.type)) return false;
    seen.add(item.id);
    return true;
  }).map((item, index) => ({
    id: `${item.id}`.slice(0, 120), type: item.type,
    ...(item.type === 'analytic' ? { sheetId: item.sheetId, analyticId: item.analyticId } : {}),
    ...(item.type === 'dimension' ? { dimensionId: item.dimensionId } : {}),
    order: index,
    colSpan: clampWidth(item.colSpan, item.type === 'time' ? 12 : item.type === 'analytic' ? 6 : 4),
    rowSpan: Math.max(1, Math.min(3, Number(item.rowSpan) || (item.type === 'analytic' ? 2 : 1)))
  }));
  if (!items.some((item) => item.type === 'time')) {
    items.unshift({ id: 'dashboard_time', type: 'time', order: 0, colSpan: 12, rowSpan: 1 });
  }
  return {
    version: DASHBOARD_CONFIG_VERSION,
    defaultTimeRange: {
      preset: DASHBOARD_TIME_PRESETS.includes(source?.defaultTimeRange?.preset) ? source.defaultTimeRange.preset : 'all',
      start: `${source?.defaultTimeRange?.start || ''}`.slice(0, 10),
      end: `${source?.defaultTimeRange?.end || ''}`.slice(0, 10),
      timezone: `${source?.defaultTimeRange?.timezone || getBrowserTimeZone()}`.slice(0, 80)
    },
    dimensions,
    items: items.map((item, index) => ({ ...item, order: index }))
  };
};

export const suggestDashboardDimensions = (sheets = [], existingDimensions = []) => {
  const existingKeys = new Set((existingDimensions || []).map((dimension) => `${slug(dimension.name)}:${dimension.type}`));
  const groups = new Map();
  (sheets || []).forEach((sheet) => (sheet?.headers || []).forEach((header) => {
    if (!header?.id || !header?.label) return;
    const family = dashboardFieldFamily(header.type);
    const key = `${slug(header.label)}:${family}`;
    if (!groups.has(key)) groups.set(key, { name: header.label, type: family, mappings: [] });
    groups.get(key).mappings.push({ sheetId: sheet.id, headerId: header.id });
  }));
  return [...groups.entries()].filter(([key, group]) => !existingKeys.has(key) && new Set(group.mappings.map((item) => item.sheetId)).size >= 2)
    .map(([key, group]) => ({ ...group, id: uniqueId('dimension', key) }));
};

export const dashboardTimeLabel = (preset) => ({
  all: 'All time', last_7_days: 'Last 7 days', last_30_days: 'Last 30 days',
  last_90_days: 'Last 90 days', this_month: 'This month', this_quarter: 'This quarter',
  this_year: 'This year', custom: 'Custom range'
}[preset] || 'All time');
